import { ComponentType as AddressComponentType, Alignment, BooleanComponentType, ArrayComponentType, ImageFit, InputType, NumberComponentType, NumberOfColumns, PaymentComponentType, SchedulingComponentType, StaffStrategySelection, StringComponentType, UploadFileFormat, WixFileComponentType, ObjectArrayComponentType, PriceType } from '@wix/ambassador-forms-v2-form-template/types';
import { toV1QuestionImageProperties } from '../label-media-properties';
import { transformFieldToV1 } from './transform-field-to-v1';
const idKey = 'id';
const LabelKey = 'label';
const PlaceholderKey = 'placeholder';
const DescriptionKey = 'description';
const HideLabelKey = 'hideLabel';
const ShowDateLabelsKey = 'showDateLabels';
const HidePlaceholderKey = 'hidePlaceholder';
const Use24HourFormatKey = 'use24HourFormat';
const AddOtherKey = 'addOther';
const AddOtherLabelKey = 'addOtherLabel';
const NumberOfColumnsKey = 'numberOfColumns';
const AddOtherPlaceholderKey = 'addOtherPlaceholder';
const DefaultValueKey = 'defaultValue';
const ValueKey = 'value';
const OptionsKey = 'options';
const ButtonTextKey = 'buttonText';
const ValueSliderLimitKey = 'valueSliderLimit';
const UploadFileFormatsKey = 'uploadFileFormats';
const ExplanationTextKey = 'explanationText';
const MediaKey = 'media';
const UploadEnabledKey = 'uploadEnabled';
const FirstDayOfWeekKey = 'firstDayOfWeek';
const DefaultCountryCodeKey = 'defaultCountry';
const ImageResizeKey = 'imageResize';
const ChoiceImageResizeKey = 'choiceImageResize';
const ChoiceImageAlignmentKey = 'choiceImageAlignment';
const ShowFlagKey = 'showFlag';
const NameKey = 'name';
const DurationKey = 'duration';
const StaffIdsKey = 'staffIds';
const ManualApprovalRequiredKey = 'manualApprovalRequired';
const RebookingPolicyKey = 'rebookingPolicy';
const FormatKey = 'format';
const FormatDescriptionTypeKey = 'formatDescriptionType';
const LocationTypeKey = 'locationType';
const FormatDescriptionKey = 'formatDescription';
const LocationIdsKey = 'locationIds';
const MeetingTypeKey = 'meetingType';
const StaffStrategySelectionKey = 'staffStrategySelection';
const RepeaterFieldsKey = 'fields';
const RepeaterLayoutKey = 'layout';
const ScoreKey = 'score';
const CorrectAnswersKey = 'correctAnswers';
const EmailOptionsKey = 'emailOptions';
export function makeViewOfInputField(inputOptions) {
    switch(inputOptions.inputType){
        case InputType.STRING:
            return resolveStringComponent(inputOptions.stringOptions);
        case InputType.NUMBER:
            return resolveNumberComponent(inputOptions.numberOptions);
        case InputType.BOOLEAN:
            return resolveBooleanComponent(inputOptions.booleanOptions);
        case InputType.ARRAY:
            return resolveArrayComponent(inputOptions.arrayOptions);
        case InputType.PAYMENT:
            return resolvePaymentComponent(inputOptions.paymentOptions);
        case InputType.WIX_FILE:
            return resolveWixFileComponent(inputOptions.wixFileOptions);
        case InputType.ADDRESS:
            return resolveMultilineAddressComponent(inputOptions.addressOptions);
        case InputType.MULTILINE_ADDRESS:
            return resolveMultilineAddressComponent(inputOptions.multilineAddressOptions);
        case InputType.SCHEDULING:
            return resolveSchedulingComponent(inputOptions.schedulingOptions);
        case InputType.OBJECT_ARRAY:
            return resolveObjectArrayComponent(inputOptions.objectArrayOptions);
        default:
            throw new Error(`Unknown input type ${inputOptions.inputType}`);
    }
}
function resolveWixFileComponent(wixFileComponent) {
    switch(wixFileComponent.componentType){
        case WixFileComponentType.FILE_UPLOAD:
            return takeWixFileInputViewProperties(wixFileComponent.fileUploadOptions, wixFileComponent.validation);
        case WixFileComponentType.SIGNATURE:
            return takeSignatureInputViewProperties(wixFileComponent.signatureOptions);
        default:
            throw new Error(`Unknown wix file component type: ${wixFileComponent.componentType}`);
    }
}
function takeWixFileInputViewProperties(fileUpload, validation) {
    const fileFormats = (validation.uploadFileFormats ?? []).map((format)=>{
        switch(format){
            case UploadFileFormat.VIDEO:
                return 'Video';
            case UploadFileFormat.IMAGE:
                return 'Image';
            case UploadFileFormat.AUDIO:
                return 'Audio';
            case UploadFileFormat.DOCUMENT:
                return 'Document';
            case UploadFileFormat.ARCHIVE:
                return 'Archive';
            case UploadFileFormat.MODEL_3D:
                return 'Model3D';
            default:
                throw new Error(`Unknown file format: ${format}`);
        }
    });
    return {
        [HideLabelKey]: !fileUpload.showLabel,
        [UploadFileFormatsKey]: fileFormats,
        [ValueSliderLimitKey]: validation.fileLimit,
        ...fileUpload.description && {
            [DescriptionKey]: fileUpload.description
        },
        ...fileUpload.label && {
            [LabelKey]: fileUpload.label
        },
        ...fileUpload.buttonText && {
            [ButtonTextKey]: fileUpload.buttonText
        },
        ...fileUpload.explanationText && {
            [ExplanationTextKey]: fileUpload.explanationText
        }
    };
}
function takeSignatureInputViewProperties(signature) {
    return {
        [UploadEnabledKey]: signature.imageUploadEnabled,
        [HideLabelKey]: !signature.showLabel,
        ...signature.label && {
            [LabelKey]: signature.label
        },
        ...signature.description && {
            [DescriptionKey]: signature.description
        }
    };
}
function resolveArrayComponent(arrayComponent) {
    const quizSettings = resolveQuizFieldSettings(arrayComponent.quizFieldSettings);
    switch(arrayComponent.componentType){
        case ArrayComponentType.CHECKBOX_GROUP:
        case 'IMAGE_CHOICE':
            return {
                ...takeCheckboxGroupViewProperties(arrayComponent.checkboxGroupOptions),
                ...quizSettings
            };
        case ArrayComponentType.SERVICES_CHECKBOX_GROUP:
            return {
                ...takeCheckboxGroupViewProperties(arrayComponent.servicesCheckboxGroupOptions),
                ...quizSettings
            };
        case ArrayComponentType.TAGS:
            return {
                ...takeTagsViewProperties(arrayComponent.tagsOptions),
                ...quizSettings
            };
        default:
            throw new Error(`Unknown array component type: ${arrayComponent.componentType}`);
    }
}
function takeCheckboxGroupViewProperties(checkboxGroup) {
    const labelMedia = toV1QuestionImageProperties(checkboxGroup.media, checkboxGroup.mediaSettings);
    return {
        [HideLabelKey]: !checkboxGroup.showLabel,
        [AddOtherKey]: !!checkboxGroup.customOption,
        [OptionsKey]: checkboxGroup.options.map((option)=>makeMediaOptionView(option)),
        [NumberOfColumnsKey]: numericNumberOfColumns(checkboxGroup.numberOfColumns ?? NumberOfColumns.ONE),
        [DefaultValueKey]: checkboxGroup.options.filter((option)=>option.default).map((option)=>option.value),
        ...checkboxGroup.description && {
            [DescriptionKey]: checkboxGroup.description
        },
        ...checkboxGroup.label && {
            [LabelKey]: checkboxGroup.label
        },
        ...checkboxGroup.customOption && {
            [AddOtherLabelKey]: checkboxGroup.customOption.label,
            [AddOtherPlaceholderKey]: checkboxGroup.customOption.placeholder
        },
        ...checkboxGroup.optionsMediaSettings?.imageFit && {
            [ChoiceImageResizeKey]: checkboxGroup.optionsMediaSettings.imageFit === ImageFit.CONTAIN ? 'contain' : 'cover'
        },
        ...checkboxGroup.optionsMediaSettings?.imageAlignment && {
            [ChoiceImageAlignmentKey]: checkboxGroup.optionsMediaSettings.imageAlignment === Alignment.LEFT ? 'left' : checkboxGroup.optionsMediaSettings.imageAlignment === Alignment.RIGHT ? 'right' : 'center'
        },
        ...labelMedia
    };
}
function takeTagsViewProperties(tags) {
    return {
        [HideLabelKey]: !tags.showLabel,
        [AddOtherKey]: !!tags.customOption,
        [OptionsKey]: tags.options.map((option)=>makeOptionView(option)),
        [NumberOfColumnsKey]: numericNumberOfColumns(tags.numberOfColumns ?? NumberOfColumns.ONE),
        [DefaultValueKey]: tags.options.filter((option)=>option.default).map((option)=>option.value),
        ...tags.description && {
            [DescriptionKey]: tags.description
        },
        ...tags.label && {
            [LabelKey]: tags.label
        },
        ...tags.customOption && {
            [AddOtherLabelKey]: tags.customOption.label,
            [AddOtherPlaceholderKey]: tags.customOption.placeholder
        }
    };
}
function makePaymentDefaultValue(options, paymentValidation) {
    const products = paymentValidation?.products ?? [];
    const result = options.filter((option)=>option.default).map((option)=>{
        const product = products.find((p)=>p.id === option.value);
        const price = product?.priceType === PriceType.FIXED_PRICE ? product.fixedPriceOptions?.price : '0';
        return {
            productId: option.value,
            price: price ?? '0',
            quantity: 1
        };
    });
    return result.length > 0 ? result : undefined;
}
function takeProductCheckboxGroupViewProperties(checkboxGroup, paymentValidation) {
    const defaultValue = makePaymentDefaultValue(checkboxGroup.options, paymentValidation);
    return {
        [HideLabelKey]: !checkboxGroup.showLabel,
        [ImageResizeKey]: checkboxGroup.imageFit === ImageFit.CONTAIN ? 'FIT' : 'CROP',
        [OptionsKey]: checkboxGroup.options.map((option)=>makeOptionView(option)),
        ...defaultValue && {
            [DefaultValueKey]: defaultValue
        },
        ...checkboxGroup.description && {
            [DescriptionKey]: checkboxGroup.description
        },
        ...checkboxGroup.label && {
            [LabelKey]: checkboxGroup.label
        }
    };
}
function resolveBooleanComponent(booleanComponent) {
    switch(booleanComponent.componentType){
        case BooleanComponentType.CHECKBOX:
            return takeCheckBoxInputViewProperties(booleanComponent.checkboxOptions);
        default:
            throw new Error(`Unknown boolean component type: ${booleanComponent.componentType}`);
    }
}
function takeCheckBoxInputViewProperties(checkBox) {
    return {
        ...checkBox.label ? {
            [LabelKey]: checkBox.label
        } : {},
        defaultValue: checkBox.checked
    };
}
function resolveNumberComponent(numberComponent) {
    const quizSettings = resolveQuizFieldSettings(numberComponent.quizFieldSettings);
    switch(numberComponent.componentType){
        case NumberComponentType.NUMBER_INPUT:
            return {
                ...takeNumberInputViewProperties(numberComponent.numberInputOptions),
                ...quizSettings
            };
        case NumberComponentType.RATING_INPUT:
            return {
                ...takeRatingInputViewProperties(numberComponent.ratingInputOptions),
                ...quizSettings
            };
        default:
            throw new Error(`Unknown number component type: ${numberComponent.componentType}`);
    }
}
function takeNumberInputViewProperties(numberInput) {
    const labelMedia = toV1QuestionImageProperties(numberInput.media, numberInput.mediaSettings);
    return {
        [HideLabelKey]: !numberInput.showLabel,
        ...numberInput.description && {
            [DescriptionKey]: numberInput.description
        },
        ...numberInput.label && {
            [LabelKey]: numberInput.label
        },
        ...numberInput.placeholder && {
            [PlaceholderKey]: numberInput.placeholder
        },
        ...typeof numberInput.default === 'number' && {
            [DefaultValueKey]: numberInput.default
        },
        ...labelMedia
    };
}
function takeRatingInputViewProperties(ratingInput) {
    return {
        [HideLabelKey]: !ratingInput.showLabel,
        ...ratingInput.defaultValue && {
            [DefaultValueKey]: ratingInput.defaultValue
        },
        ...ratingInput.description && {
            [DescriptionKey]: ratingInput.description
        },
        ...ratingInput.label && {
            [LabelKey]: ratingInput.label
        }
    };
}
function resolveStringComponent(stringComponent) {
    const quizSettings = resolveQuizFieldSettings(stringComponent.quizFieldSettings);
    switch(stringComponent.componentType){
        case StringComponentType.TEXT_INPUT:
            return {
                ...takeTextInputViewProperties(stringComponent.textInputOptions),
                ...quizSettings
            };
        case StringComponentType.PASSWORD:
            return {
                ...takePasswordViewProperties(stringComponent.passwordOptions),
                ...quizSettings
            };
        case StringComponentType.RADIO_GROUP:
            return {
                ...takeRadioGroupViewProperties(stringComponent.radioGroupOptions),
                ...quizSettings
            };
        case StringComponentType.DROPDOWN:
            return {
                ...takeDropdownViewProperties(stringComponent.dropdownOptions),
                ...quizSettings
            };
        case StringComponentType.DATE_TIME:
            return {
                ...takeDateTimeViewProperties(stringComponent.dateTimeOptions),
                ...quizSettings
            };
        case StringComponentType.DATE_INPUT:
            return {
                ...takeDateInputViewProperties(stringComponent.dateInputOptions),
                ...quizSettings
            };
        case StringComponentType.TIME_INPUT:
            return {
                ...takeTimeInputViewProperties(stringComponent.timeInputOptions),
                ...quizSettings
            };
        case StringComponentType.DATE_PICKER:
            return {
                ...takeDatePickerViewProperties(stringComponent.datePickerOptions),
                ...quizSettings
            };
        case StringComponentType.PHONE_INPUT:
            return {
                ...takePhoneInputViewProperties(stringComponent.phoneInputOptions),
                ...quizSettings
            };
        case StringComponentType.SERVICES_DROPDOWN:
            return {
                ...takeServiceDropdownViewProperties(stringComponent.servicesDropdownOptions),
                ...quizSettings
            };
        default:
            throw new Error(`Unknown string component type: ${stringComponent.componentType}`);
    }
}
function takeTextInputViewProperties(textInput) {
    const labelMedia = toV1QuestionImageProperties(textInput.media, textInput.mediaSettings);
    return {
        [HideLabelKey]: !textInput.showLabel,
        ...textInput.description && {
            [DescriptionKey]: textInput.description
        },
        ...textInput.label && {
            [LabelKey]: textInput.label
        },
        ...textInput.placeholder && {
            [PlaceholderKey]: textInput.placeholder
        },
        ...textInput.default && {
            [DefaultValueKey]: textInput.default
        },
        ...textInput.emailOptions && {
            [EmailOptionsKey]: textInput.emailOptions
        },
        ...labelMedia
    };
}
function takePasswordViewProperties(password) {
    return {
        [HideLabelKey]: !password.showLabel,
        ...password.description && {
            [DescriptionKey]: password.description
        },
        ...password.label && {
            [LabelKey]: password.label
        },
        ...password.placeholder && {
            [PlaceholderKey]: password.placeholder
        },
        ...password.default && {
            [DefaultValueKey]: password.default
        }
    };
}
function takeRadioGroupViewProperties(radioGroup) {
    const options = radioGroup.options ?? [];
    const labelMedia = toV1QuestionImageProperties(radioGroup.media, radioGroup.mediaSettings);
    return {
        [HideLabelKey]: !radioGroup.showLabel,
        [AddOtherKey]: !!radioGroup.customOption,
        [NumberOfColumnsKey]: numericNumberOfColumns(radioGroup.numberOfColumns ?? NumberOfColumns.ONE),
        [OptionsKey]: options.map((option)=>makeOptionView(option)),
        ...radioGroup.description && {
            [DescriptionKey]: radioGroup.description
        },
        ...radioGroup.label && {
            [LabelKey]: radioGroup.label
        },
        ...radioGroup.customOption && {
            [AddOtherLabelKey]: radioGroup.customOption.label,
            [AddOtherPlaceholderKey]: radioGroup.customOption.placeholder
        },
        ...options.find((option)=>option.default) && {
            [DefaultValueKey]: options.find((option)=>option.default)?.value
        },
        ...labelMedia
    };
}
function takeDropdownViewProperties(dropdown) {
    const options = dropdown.options ?? [];
    return {
        [HideLabelKey]: !dropdown.showLabel,
        [AddOtherKey]: !!dropdown.customOption,
        [OptionsKey]: options.map((option)=>makeOptionView(option)),
        ...dropdown.description && {
            [DescriptionKey]: dropdown.description
        },
        ...dropdown.label && {
            [LabelKey]: dropdown.label
        },
        ...dropdown.placeholder && {
            [PlaceholderKey]: dropdown.placeholder
        },
        ...dropdown.customOption && {
            [AddOtherLabelKey]: dropdown.customOption.label,
            [AddOtherPlaceholderKey]: dropdown.customOption.placeholder
        },
        ...options.find((option)=>option.default) && {
            [DefaultValueKey]: options.find((option)=>option.default)?.value
        }
    };
}
function takeDateTimeViewProperties(dateTimeInput) {
    return {
        [HideLabelKey]: !dateTimeInput.showLabel,
        [ShowDateLabelsKey]: dateTimeInput.showDateLabels,
        [HidePlaceholderKey]: !dateTimeInput.showPlaceholder,
        ...dateTimeInput.description && {
            [DescriptionKey]: dateTimeInput.description
        },
        ...dateTimeInput.label && {
            [LabelKey]: dateTimeInput.label
        },
        ...dateTimeInput.default && {
            [DefaultValueKey]: dateTimeInput.default
        },
        [Use24HourFormatKey]: dateTimeInput?.use24HourFormat
    };
}
function takeDateInputViewProperties(dateInput) {
    return {
        [HideLabelKey]: !dateInput.showLabel,
        [ShowDateLabelsKey]: dateInput.showDateLabels,
        [HidePlaceholderKey]: !dateInput.showPlaceholder,
        ...dateInput.description && {
            [DescriptionKey]: dateInput.description
        },
        ...dateInput.label && {
            [LabelKey]: dateInput.label
        },
        ...dateInput.default && {
            [DefaultValueKey]: dateInput.default
        }
    };
}
function takeTimeInputViewProperties(timeInput) {
    return {
        [HideLabelKey]: !timeInput.showLabel,
        [HidePlaceholderKey]: !timeInput.showPlaceholder,
        ...timeInput.description && {
            [DescriptionKey]: timeInput.description
        },
        ...timeInput.label && {
            [LabelKey]: timeInput.label
        },
        ...timeInput.default && {
            [DefaultValueKey]: timeInput.default
        },
        [Use24HourFormatKey]: timeInput.use24HourFormat
    };
}
function takeDatePickerViewProperties(datePicker) {
    return {
        [HideLabelKey]: !datePicker.showLabel,
        ...datePicker.placeholder && {
            [PlaceholderKey]: datePicker.placeholder
        },
        ...datePicker.description && {
            [DescriptionKey]: datePicker.description
        },
        ...datePicker.label && {
            [LabelKey]: datePicker.label
        },
        ...datePicker.default && {
            [DefaultValueKey]: datePicker.default
        },
        [FirstDayOfWeekKey]: datePicker?.firstDayOfWeek
    };
}
function takePhoneInputViewProperties(phoneInput) {
    return {
        [HideLabelKey]: !phoneInput.showLabel,
        ...phoneInput.label && {
            [LabelKey]: phoneInput.label
        },
        ...phoneInput.placeholder && {
            [PlaceholderKey]: phoneInput.placeholder
        },
        ...phoneInput.description && {
            [DescriptionKey]: phoneInput.description
        },
        ...phoneInput.defaultCountryCode && {
            [DefaultCountryCodeKey]: phoneInput.defaultCountryCode
        },
        [ShowFlagKey]: phoneInput.showCountryFlag
    };
}
function takeServiceDropdownViewProperties(servicesDropdownOptions) {
    const options = servicesDropdownOptions.options ?? [];
    return {
        [HideLabelKey]: !servicesDropdownOptions.showLabel,
        [AddOtherKey]: !!servicesDropdownOptions.customOption,
        [OptionsKey]: options.map((option)=>makeOptionView(option)),
        ...servicesDropdownOptions.label && {
            [LabelKey]: servicesDropdownOptions.label
        },
        ...servicesDropdownOptions.placeholder && {
            [PlaceholderKey]: servicesDropdownOptions.placeholder
        },
        ...servicesDropdownOptions.customOption && {
            [AddOtherLabelKey]: servicesDropdownOptions.customOption.label,
            [AddOtherPlaceholderKey]: servicesDropdownOptions.customOption.placeholder
        }
    };
}
function resolvePaymentComponent(paymentComponent) {
    switch(paymentComponent.componentType){
        case PaymentComponentType.CHECKBOX_GROUP:
            return takeProductCheckboxGroupViewProperties(paymentComponent.checkboxGroupOptions, paymentComponent.validation);
        case PaymentComponentType.DONATION_INPUT:
            return takeDonationsInputViewProperties(paymentComponent.donationInputOptions, paymentComponent.validation);
        case PaymentComponentType.FIXED_PAYMENT:
            return takeFixedPaymentViewProperties(paymentComponent.fixedPaymentOptions);
        case PaymentComponentType.PAYMENT_INPUT:
            return takePaymentInputViewProperties(paymentComponent.paymentInputOptions);
        default:
            throw new Error(`Unknown payment component type: ${paymentComponent.componentType}`);
    }
}
function resolveMultilineAddressComponent(multilineAddress) {
    switch(multilineAddress.componentType){
        case AddressComponentType.MULTILINE_ADDRESS:
            return takeMultilineAddressViewProperties(multilineAddress.multilineAddressOptions);
        default:
            throw new Error(`Unknown multiline address component type: ${multilineAddress.componentType}`);
    }
}
function resolveSchedulingComponent(schedulingOptions) {
    switch(schedulingOptions.componentType){
        case SchedulingComponentType.APPOINTMENT:
            return takeAppointmentViewProperties(schedulingOptions.appointmentOptions);
        default:
            throw new Error(`Unknown scheduling component type: ${schedulingOptions.componentType}`);
    }
}
function takeMultilineAddressViewProperties(multilineAddress) {
    const { showLabel, ...multilineAddressView } = multilineAddress;
    return {
        ...multilineAddressView,
        hideLabel: !showLabel
    };
}
const staffStrategyV2toV1 = {
    [StaffStrategySelection.AUTOMATIC]: 'AUTOMATIC',
    [StaffStrategySelection.HIGH_TO_LOW_PRIORITY]: 'PRIORITY_HIGH_TO_LOW',
    [StaffStrategySelection.LOW_TO_HIGH_PRIORITY]: 'PRIORITY_LOW_TO_HIGH'
};
function takeAppointmentViewProperties(appointmentOptions) {
    const { label, name, durationInMinutes, manualApprovalRequired, staffIds, format, description, showLabel, meetingType, staffStrategySelection, phoneOptions, videoConferenceOptions, inPersonOptions, rebookingPolicy } = appointmentOptions;
    return {
        ...label && {
            [LabelKey]: label
        },
        ...name && {
            [NameKey]: name
        },
        ...durationInMinutes && {
            [DurationKey]: durationInMinutes
        },
        ...manualApprovalRequired !== undefined && {
            [ManualApprovalRequiredKey]: manualApprovalRequired
        },
        ...description && {
            [DescriptionKey]: description
        },
        ...showLabel !== undefined && {
            [HideLabelKey]: !showLabel
        },
        [FormatKey]: format,
        [FormatDescriptionTypeKey]: format,
        [StaffIdsKey]: staffIds,
        ...meetingType && {
            [MeetingTypeKey]: meetingType
        },
        ...staffStrategySelection && {
            [StaffStrategySelectionKey]: staffStrategyV2toV1[staffStrategySelection] ?? staffStrategySelection
        },
        ...rebookingPolicy !== undefined && {
            [RebookingPolicyKey]: rebookingPolicy
        },
        ...transformFormatInfoToView(inPersonOptions, videoConferenceOptions, phoneOptions)
    };
}
function transformFormatInfoToView(inPersonOptions, videoConferenceOptions, phoneOptions) {
    if (phoneOptions?.description) {
        return {
            [FormatDescriptionKey]: phoneOptions.description
        };
    }
    if (videoConferenceOptions?.description) {
        return {
            [FormatDescriptionKey]: videoConferenceOptions.description
        };
    }
    if (inPersonOptions) {
        const { locations } = inPersonOptions;
        const customLocation = (locations ?? []).find((loc)=>loc.customAddress);
        if (customLocation) {
            return {
                [FormatDescriptionKey]: customLocation.customAddress,
                [LocationTypeKey]: 'CUSTOM_LOCATION'
            };
        }
        const businessIds = (locations ?? []).map((loc)=>loc.businessLocationId).filter(Boolean);
        if (businessIds.length > 0) {
            return {
                [LocationIdsKey]: businessIds,
                [LocationTypeKey]: 'OS_LOCATION'
            };
        }
    }
    return {};
}
function takeDonationsInputViewProperties(donationInput, paymentValidation) {
    const options = donationInput.options ?? [];
    const defaultValue = makePaymentDefaultValue(options, paymentValidation);
    return {
        [HideLabelKey]: !donationInput.showLabel,
        [AddOtherKey]: !!donationInput.customOption,
        [NumberOfColumnsKey]: numericNumberOfColumns(donationInput.numberOfColumns ?? NumberOfColumns.ONE),
        [OptionsKey]: options.map((option)=>makeOptionView(option)),
        ...donationInput.description && {
            [DescriptionKey]: donationInput.description
        },
        ...donationInput.label && {
            [LabelKey]: donationInput.label
        },
        ...donationInput.customOption && {
            [AddOtherLabelKey]: donationInput.customOption.label,
            [AddOtherPlaceholderKey]: donationInput.customOption.placeholder
        },
        ...defaultValue && {
            [DefaultValueKey]: defaultValue
        }
    };
}
function takeFixedPaymentViewProperties(fixedPayment) {
    return {
        [HideLabelKey]: !fixedPayment.showLabel,
        ...fixedPayment.label && {
            [LabelKey]: fixedPayment.label
        },
        ...fixedPayment.description && {
            [DescriptionKey]: fixedPayment.description
        }
    };
}
function takePaymentInputViewProperties(paymentInput) {
    return {
        [HideLabelKey]: !paymentInput.showLabel,
        ...paymentInput.label && {
            [LabelKey]: paymentInput.label
        },
        ...paymentInput.description && {
            [DescriptionKey]: paymentInput.description
        },
        ...paymentInput.placeholder && {
            [PlaceholderKey]: paymentInput.placeholder
        },
        ...typeof paymentInput.default === 'number' && {
            [DefaultValueKey]: paymentInput.default
        }
    };
}
function makeOptionView(option) {
    return {
        [LabelKey]: option.label,
        [ValueKey]: option.value,
        [idKey]: option.id,
        ...option.media && {
            [MediaKey]: option.media
        },
        ...option.showLabel === false && {
            isLabelHidden: true
        }
    };
}
function makeMediaOptionView(option) {
    let media = option.media;
    if (media?.image && 'altText' in media.image) {
        const { altText, ...imageWithoutAltText } = media.image;
        media = {
            ...media,
            image: imageWithoutAltText,
            ...altText !== undefined && {
                altText
            }
        };
    }
    return {
        [LabelKey]: option.label,
        [ValueKey]: option.value,
        [idKey]: option.id,
        ...media && {
            [MediaKey]: media
        },
        ...option.showLabel === false && {
            isLabelHidden: true
        }
    };
}
function numericNumberOfColumns(numberOfColumnsEnum) {
    switch(numberOfColumnsEnum){
        case 'ZERO':
            return 0;
        case 'TWO':
            return 2;
        case 'THREE':
            return 3;
        default:
            return 1;
    }
}
function resolveObjectArrayComponent(objectArrayComponent) {
    switch(objectArrayComponent.componentType){
        case ObjectArrayComponentType.REPEATER:
            return takeRepeaterViewProperties(objectArrayComponent.repeaterOptions);
        default:
            throw new Error(`Unknown object array component type: ${objectArrayComponent.componentType}`);
    }
}
function takeRepeaterViewProperties(repeater) {
    return {
        [LabelKey]: repeater.label,
        [HideLabelKey]: !repeater.showLabel,
        [RepeaterFieldsKey]: repeater.fields?.map(transformFieldToV1),
        [RepeaterLayoutKey]: repeater.layout,
        ...repeater.description && {
            [DescriptionKey]: repeater.description
        }
    };
}
function resolveQuizFieldSettings(quizFieldSettings) {
    const correctAnswers = quizFieldSettings?.correctAnswersList?.correctAnswers;
    return {
        ...quizFieldSettings?.score !== undefined && {
            [ScoreKey]: quizFieldSettings.score
        },
        ...Array.isArray(correctAnswers) && {
            [CorrectAnswersKey]: correctAnswers
        }
    };
}

//# sourceMappingURL=make-view-of-input-field.js.map